import type { QuietHours } from './types';

const WEEKDAY_ISO: Record<string, number> = {
  Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7,
};

interface ZonedParts {
  minutes: number;
  isoWeekday: number;
}

/** Wall-clock minutes + ISO weekday in the user's quiet-hours timezone. */
function zonedParts(at: Date, timezone: string): ZonedParts {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone, hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
    }).formatToParts(at);
  } catch {
    /* Invalid IANA zone — fall back to the browser's own zone rather than failing open. */
    parts = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
    }).formatToParts(at);
  }

  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '0';

  const hour = Number(read('hour')) % 24;
  return {
    minutes: hour * 60 + Number(read('minute')),
    isoWeekday: WEEKDAY_ISO[read('weekday')] ?? 1,
  };
}

/** "22:30" -> 1350. Returns null for malformed input. */
function toMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** True while the quiet window is open. Windows may wrap past midnight. */
export function isWithinQuietHours(quiet: QuietHours, at: Date = new Date()): boolean {
  if (!quiet.enabled) return false;

  const start = toMinutes(quiet.start);
  const end = toMinutes(quiet.end);
  if (start === null || end === null || start === end) return false;

  const { minutes, isoWeekday } = zonedParts(at, quiet.timezone);
  if (quiet.days.length > 0 && !quiet.days.includes(isoWeekday)) return false;

  return start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}
